import React, { createContext, useContext, useState, useEffect } from "react";
import { decodeTokenService } from "../services/decodeTokenService";
import { Language } from "../utils/dictionary";
import { Country } from "../const/countryList";

interface AuthContextType {
  setLanguage: (language: Language) => void;
  setCountry: (country: Country) => void;
  setTimezone: (timezone: string) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (access_token: string, refresh_token: string) => void;
  logout: () => void;
  refreshAuth: () => void;
  isUserFake: boolean;
  language: Language;
  country: Country;
  timezone: string;
  credits: number;
  setCredits: (credits:number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getInitialLanguage = (): Language => {
  const savedLanguage = localStorage.getItem("user_language");
  if (savedLanguage && Object.values(Language).includes(savedLanguage as Language)) {
    return savedLanguage as Language;
  }
  return Language.SPANISH;
};

const getInitialCountry = (): Country => {
  const savedCountry = localStorage.getItem("user_country");
  if (savedCountry && Object.values(Country).includes(savedCountry as Country)) {
    return savedCountry as Country;
  }
  return Country.COLOMBIA;
};

const getInitialTimezone = (): string => {
  const saved = localStorage.getItem("user_timezone");
  if (saved) return saved;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/Bogota";
  }
};

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [isUserFake, setIsUserFake] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Prevent premature redirects
  const [language, _setLanguage] = useState<Language>(getInitialLanguage);
  const [country, setCountry] = useState<Country>(getInitialCountry);
  const [timezone, setTimezone] = useState<string>(getInitialTimezone);
  const [credits, setCredits] = useState<number>(0);

  const setLanguage = (lang: Language) => {
    localStorage.setItem("user_language", lang);
    _setLanguage(lang);
  };

  const setTimezonePersist = (tz: string) => {
    localStorage.setItem("user_timezone", tz);
    setTimezone(tz);
  };

  const checkAuthentication = () => {
    setIsLoading(true);
    try {
      const access_token = localStorage.getItem("access_token");
      const refresh_token = localStorage.getItem("refresh_token");

      if (access_token || refresh_token) {
        const user_id = decodeTokenService.getUserIdFromToken()
        
        if (user_id === import.meta.env.VITE_REACT_APP_USER_ID_FAKE) {
          setIsUserFake(true);
        }
        setIsAuthenticated(true);
      
      } else {
        setIsAuthenticated(false);
      }

    } catch (error) {
      console.error("Error validating authentication:", error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = (access_token: string, refresh_token: string) => {
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);
    setIsAuthenticated(true);
    const isMobile = window.innerWidth < 768;
    const redirectPath = isMobile ? "/settings" : "/home";
    window.location.href = redirectPath;
  };

  const logout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    // localStorage.removeItem("user_language");
    setIsAuthenticated(false);
    // setLanguageState(Language.SPANISH);
  };

  const refreshAuth = () => {
    checkAuthentication();
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");

    if (token) {
      localStorage.setItem("access_token", token);
      const userId = decodeTokenService.getUserIdFromToken();
      if (userId) {
        localStorage.setItem("user_id", userId);
      }
      
      const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
      window.history.replaceState({ path: newUrl }, "", newUrl);
    }

    checkAuthentication();
  }, []);

  if (isLoading) {
    return <div>Loading Authentication...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshAuth,
        isUserFake,
        language,
        setLanguage,
        country,
        setCountry,
        timezone,
        setTimezone: setTimezonePersist,
        credits,
        setCredits,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
